import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useEffect, useMemo, useState } from 'react';

import { ICreditSubmissionProps } from '../datasource/mutations/creditSubmission';
import { useGetCreditSubmissionMessagesQuery } from '../datasource/queries/cmsMessages';
import { useGetLicensesQuery } from '../datasource/queries/licenses';
import { ExternalCommand, setExternalCommand } from '../stories/Store/playerSlice';
import { useAppDispatch, useAppSelector } from '.';

export const useRequestCredit = () => {
    const dispatch = useAppDispatch();
    const isWebcast = useAppSelector((state) => state.player.launchDetails.isWebcast ?? false);
    const licenses = useAppSelector((state) => state.requestCredit.licenses);
    const disabledIndvLicenseSks = useAppSelector((state) => state.requestCredit.disabledIndvLicenseSks);
    const authCode = useAppSelector((state) => state.requestCredit.authorizationCode);
    const encryptedRegistrationId = useAppSelector((state) => state.requestCredit.encryptedRegistrationId);

    const { isError: isGetLicensesError } = useGetLicensesQuery(!encryptedRegistrationId ? skipToken : { encryptedRegistrationId });

    const activeIndvLicenses = useMemo(
        () => licenses.filter(l => !disabledIndvLicenseSks.some(d => d === l.indvStateLicense_SK)),
        [disabledIndvLicenseSks, licenses]);

    const creditSubmissionProps : ICreditSubmissionProps | undefined = useMemo(() => {
        if (!encryptedRegistrationId)
            return;
        if (!activeIndvLicenses.length)
            return;

        return {
            encryptedRegistrationId: encryptedRegistrationId,
            requestedIndvLicenseSks: activeIndvLicenses.map(l => l.indvStateLicense_SK ?? 0),
            authorizationCode: authCode,
        };
    }, [activeIndvLicenses, encryptedRegistrationId, authCode]);

    const submissionMessages = useGetCreditSubmissionMessagesQuery();
    const messages = useMemo(() => {
        return {
            title: submissionMessages.data?.requestCreditPanelTitle ?? 'Here’s what you’re submitting',
            subTitle: submissionMessages.data?.requestCreditPanelSubtitle ?? 'Please verify the below information, and only request credit once you’ve completed all segments you’d like to submit.',
            acknowledgement: submissionMessages.data?.requestCreditAcknowledgement ?? 'By clicking Continue I have acknowledged receipt of the program materials, electronically or otherwise, and certify that I have participated in the above program for the time indicated. Therefore, I request that I be awarded the applicable number of credits for this program.',
            acknowledgementPortable: submissionMessages.data?.requestCreditDownloadAcknowledgement ?? 'By clicking Continue I have acknowledged receipt of the program materials, electronically or otherwise, and certify that I have participated in the above program for the time indicated. Therefore, I request that I be awarded the applicable number of credits for this program.',
            confirmationTitle: submissionMessages.data?.requestCreditConfirmationTitle ?? 'Credit request received',
            confirmationBody: submissionMessages.data?.requestCreditConfirmationBodyRichText ?? submissionMessages.data?.requestCreditConfirmationBody ?? 'Thank you for watching. Your credit request has been successfully submitted. You\'ll receive your certificate of completion by email. To learn more about certificate issuance, select When will I receive my certificate? below. To view your earned certificates, visit View all certificates.',
            confirmationButtonText: submissionMessages.data?.requestCreditButtonText ?? 'Explore more programs at PLI',
            confirmationButtonUrl: submissionMessages.data?.requestCreditConfirmationButtonUrl ?? 'https://www.pli.edu',
            certificateHelpLabel: submissionMessages.data?.requestCreditCertificateHelpLabel ?? 'When will I receive my certificate?',
            certificateHelpUrl: submissionMessages.data?.requestCreditCertificateHelpUrl ?? 'https://help.pli.edu/s/article/When-should-I-request-credit',
            viewCertificatesLabel: submissionMessages.data?.requestCreditViewCertificatesLabel ?? 'View all certificates',
            viewCertificatesUrl: submissionMessages.data?.requestCreditViewCertificatesUrl ?? 'https://www.pli.edu/account/credit/certificates',
            recommendationsTitle: submissionMessages.data?.requestCreditRecommendationsTitle ?? 'You may also like',
        };
    }, [submissionMessages]);

    const isGetSubmissionMessagesError = submissionMessages.isError;

    const errorMessageElementIds = useAppSelector((state) => state.requestCredit.errorMessageElementIds);
    const [isContinueButtonDisabled, setIsContinueButtonDisabled] = useState(false);
    useEffect(() => {
        if (errorMessageElementIds.length > 0) {
            setIsContinueButtonDisabled(true);
        } else {
            setIsContinueButtonDisabled(false);
        }
    }, [errorMessageElementIds]);

    useEffect(() => {
        !isWebcast && dispatch(setExternalCommand(ExternalCommand.pause));
    }, [dispatch, isWebcast]);

    return {
        messages,
        isContinueButtonDisabled,
        errorMessageElementIds,
        creditSubmissionProps,
        isGetLicensesError,
        isGetSubmissionMessagesError,
    };
};